import { useMemo } from 'react';
import type { ComponentType, ReactNode, SVGProps } from 'react';

import { Toast as BaseToast } from '@base-ui/react/toast';

import {
  alertContentRecipe,
  alertDescriptionRecipe,
  alertDismissRecipe,
  alertIconRecipe,
  alertRecipe,
  alertTitleRecipe,
} from '../Alert';
import CheckCircleIcon from '../Alert/check-circle.svg?react';
import CloseIcon from '../Alert/close.svg?react';
import InfoIcon from '../Alert/info.svg?react';
import WarningCircleIcon from '../Alert/warning-circle.svg?react';
import WarningIcon from '../Alert/warning.svg?react';
import { Button, IconButton } from '../Button';
import { SpinnerGlyph } from '../Spinner/SpinnerGlyph';
import { useInheritedDarkMode } from '../Theme';
import { cn } from '../utils';
import { toastActionsRecipe, toastRootRecipe, toastViewportRecipe } from './Toast.recipe';

import type { ToastOptions, ToastProviderProps, ToastSeverity, ToastViewportProps } from './types';

const ICONS: Record<ToastSeverity, ComponentType<SVGProps<SVGSVGElement>>> = {
  neutral: InfoIcon,
  info: InfoIcon,
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: WarningCircleIcon,
};

const SEVERITIES = new Set<string>(Object.keys(ICONS));

const toSeverity = (type: string | undefined): ToastSeverity =>
  type && SEVERITIES.has(type) ? (type as ToastSeverity) : 'neutral';

const ACTION_TIMEOUT = 8000;

const isLoading = (data: unknown) =>
  typeof data === 'object' && data !== null && 'loading' in data && data.loading === true;

const customIcon = (data: unknown): ReactNode =>
  typeof data === 'object' && data !== null && 'icon' in data ? (data.icon as ReactNode) : undefined;

/** The Alert's politeness, so a warning or error interrupts and a confirmation waits its turn. */
const isUrgent = (severity: ToastSeverity) => severity === 'warning' || severity === 'error';

/**
 * Design System 2.0 toast provider. Wrap the app once; render one `ToastViewport` inside it.
 *
 * Base UI's `Toast` does the lifecycle: timers that pause while the stack is hovered or focused, or
 * the window is hidden, swipe to dismiss, the F6 shortcut into the region and the live-region
 * announcements. This layer only styles it and sets Natter's defaults.
 */
export const ToastProvider = ({ children, timeout = 5000, limit = 3 }: ToastProviderProps) => (
  <BaseToast.Provider limit={limit} timeout={timeout}>
    {children}
  </BaseToast.Provider>
);

/**
 * Adds and closes toasts. `add` returns the toast's id.
 *
 * Wraps Base UI's manager so the defaults live in one place rather than at every call site: an
 * error stays until closed, a toast with an action gets 8s, warnings and errors announce
 * assertively, and pressing the action also closes the toast.
 */
export const useToastManager = () => {
  const { add, close } = BaseToast.useToastManager();

  return useMemo(
    () => ({
      add: ({
        description,
        severity: requested = 'neutral',
        title,
        action: requestedAction,
        timeout,
        id,
        loading,
        icon,
        onClose,
      }: ToastOptions) => {
        // A working toast is neutral, and offers nothing to press: the ring is the message.
        const severity = loading ? 'neutral' : requested;
        const action = loading ? undefined : requestedAction;

        // Read by the action's handler, which only runs after `add` has returned.
        const toastId: string = add({
          id,
          title,
          description,
          type: severity,
          priority: isUrgent(severity) ? 'high' : 'low',
          timeout: loading ? 0 : (timeout ?? (severity === 'error' ? 0 : action ? ACTION_TIMEOUT : undefined)),
          data: loading ? { loading: true } : icon ? { icon } : undefined,
          onClose,
          actionProps: action
            ? {
                children: action.label,
                onClick: () => {
                  action.onClick();
                  close(toastId);
                },
              }
            : undefined,
        });

        return toastId;
      },
      close: (toastId: string) => close(toastId),
    }),
    [add, close]
  );
};

/** The toast's title, else its message. `add` takes both as strings, though Base UI types them as nodes. */
const accessibleName = ({ title, description }: { title?: ReactNode; description?: ReactNode }) => {
  if (typeof title === 'string' && title) return title;

  return typeof description === 'string' ? description : undefined;
};

/**
 * The fixed region the toasts stack in: bottom-right, full width on a phone.
 *
 * Portalled to `<body>`, so the theme is carried across from where this is rendered — put it
 * inside the `.dark` region a dark surface wants its toasts to match.
 *
 * Wrap the app once in `ToastProvider` and render one `ToastViewport` inside the desired theme
 * region. A descendant calls `useToastManager().add(options)`; `description` is required. This is
 * the complete public usage path, without the private `Page` and `Fire` helpers in the stories:
 *
 * ```tsx
 * import { Button, ToastProvider, ToastViewport, useToastManager } from '@natter-co/design-system';
 *
 * const SaveButton = () => {
 *   const toast = useToastManager();
 *   return (
 *     <Button appearance="secondary" onClick={() => toast.add({ description: 'Saved', severity: 'success' })}>
 *       Save
 *     </Button>
 *   );
 * };
 *
 * export const ToastExample = () => (
 *   <ToastProvider>
 *     <SaveButton />
 *     <ToastViewport />
 *   </ToastProvider>
 * );
 * ```
 *
 * `add` returns the toast id; `toast.close(id)` closes it programmatically. Pass the same `id` in
 * `add({ id, description })` to replace a repeated message rather than stack duplicates. Set one
 * toast's `timeout: 0` to keep it until dismissal without changing the provider's default timer.
 * Errors stay by default; an action gets 8 seconds. The close control is always rendered.
 */
export const ToastViewport = ({
  className,
  closeLabel = 'Dismiss notification',
  mobilePlacement,
}: ToastViewportProps) => {
  const { toasts } = BaseToast.useToastManager();
  const { sentinelRef, inheritedDarkClass } = useInheritedDarkMode(toasts.length > 0);

  return (
    <>
      <span ref={sentinelRef} hidden />
      <BaseToast.Portal className={inheritedDarkClass}>
        <BaseToast.Viewport className={cn(toastViewportRecipe({ mobilePlacement }), className)}>
          {toasts.map((toast) => {
            const severity = toSeverity(toast.type);
            const Icon = ICONS[severity];
            const loading = isLoading(toast.data);
            const icon = customIcon(toast.data);

            return (
              <BaseToast.Root
                key={toast.id}
                className={cn(alertRecipe({ severity }), toastRootRecipe({ mobilePlacement }))}
                data-part="panel"
                // Base UI renders the panel as a dialog with no name, titled or not (axe aria-dialog-name).
                // Name it by its title when it has one, else by its message.
                aria-label={accessibleName(toast)}
                aria-busy={loading || undefined}
                data-loading={loading || undefined}
                data-severity={severity}
                swipeDirection="right"
                toast={toast}
              >
                <span aria-hidden className={alertIconRecipe()} data-part="icon">
                  {loading ? <SpinnerGlyph /> : (icon ?? <Icon aria-hidden="true" focusable="false" />)}
                </span>

                <div className={alertContentRecipe()}>
                  {toast.title && <BaseToast.Title className={alertTitleRecipe()} data-part="title" render=<p /> />}
                  <BaseToast.Description className={alertDescriptionRecipe()} data-part="description" />
                  {toast.actionProps && (
                    <div className={toastActionsRecipe()}>
                      <BaseToast.Action
                        render={
                          <Button appearance="secondary" data-part="action" size="xs">
                            {toast.actionProps.children}
                          </Button>
                        }
                      />
                    </div>
                  )}
                </div>

                <BaseToast.Close
                  render={
                    <IconButton
                      appearance="tertiary"
                      aria-label={closeLabel}
                      className={alertDismissRecipe()}
                      data-part="dismiss"
                      fill="default"
                      size="2xs"
                    >
                      <CloseIcon aria-hidden="true" focusable="false" />
                    </IconButton>
                  }
                />
              </BaseToast.Root>
            );
          })}
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </>
  );
};
